import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Brand, brandCssVars, getBrand, isProductBrand } from '../config/branding';

export interface BrandTheme {
  /** Never null — the Nextek identity when no product asked for this sign-in. */
  brand: Brand;
  /** True only when a real product sent the user here. */
  isBranded: boolean;
  /**
   * Accent custom properties for the card's root element. `{}` on the unbranded
   * path, so the card keeps its own palette.
   *
   * Must go on the same element that declares the `--accent-*` defaults (the
   * `.auth-card` / `.twofa-card` / … root): these are inline styles, which
   * outrank the stylesheet's declarations on that element. Putting them on an
   * ancestor instead would lose to the card's own rule.
   */
  style: React.CSSProperties;
}

/**
 * Which product's branding this auth screen should wear.
 *
 * Read from `?application=` in the URL and NOTHING else. Deliberately not
 * `getPendingApplication()`, which also consults the sessionStorage copy: that
 * fallback is right for the post-login handoff (it must never lose track of where
 * to send the user) but wrong for branding, because it would keep a product's
 * lockup on the plain portal sign-in for the rest of the tab's life once that
 * product had handed off even once. A bare `/` must look like Nextek.
 *
 * Branding still survives the flow's real hops, because they all keep the param
 * in the URL: every redirect that lands on the login page runs through
 * `withApplication()` (the 401 interceptor in Axios.ts, both in App.tsx,
 * NewPasswordForm), the forgot-password link carries it explicitly, and the 2FA
 * and authenticator steps render in place without navigating at all.
 *
 * So: branding follows the URL; the handoff target still follows
 * `getPendingApplication()`. Keep those two separate.
 */
export const useBrand = (): BrandTheme => {
  const { search } = useLocation();

  return useMemo(() => {
    const brand = getBrand(new URLSearchParams(search).get('application'));
    return {
      brand,
      isBranded: isProductBrand(brand),
      style: brandCssVars(brand) as React.CSSProperties,
    };
  }, [search]);
};

export default useBrand;
